import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { activityService } from '@/services/activityService';
import { formService } from '@/services/formService';
import { workOrderService } from '@/services/workOrderService';
import { projectService } from '@/services/projectService';
import { getFormUrl } from '@/utils/urlHelper';
import { PROPOSAL_STAGES } from '@/constants/proposalTypes';
import {
  getAppointmentStart,
  isTentativelyPlanned,
  getLifecycleStatus,
} from '@/utils/workOrders';
import { useToastStore } from '@/store/toastStore';
import { useUserStore } from '@/store/userStore';
import { useUIStore } from '@/store/uiStore';
import LogTimeModal from '@/components/work-orders/LogTimeModal.vue';
import WorkOrderPaymentModal from '@/components/work-orders/WorkOrderPaymentModal.vue';

export function useWorkOrderDetail(workOrderRef, { onUpdate, onComplete, onReplace } = {}) {
  const { t } = useI18n();
  const toastStore = useToastStore();
  const userStore = useUserStore();
  const uiStore = useUIStore();

  const wo = computed(() => workOrderRef.value || workOrderRef);

  // ── Notes ──────────────────────────────────────────────────────────────
  const installerNotes = ref('');
  const internalNotes = ref('');

  watch(
    wo,
    (w) => {
      if (w) {
        installerNotes.value = w.installer_notes || '';
        internalNotes.value = w.internal_notes || '';
      }
    },
    { immediate: true }
  );

  const saveInstallerNotes = async () => {
    const next = installerNotes.value || '';
    const current = wo.value?.installer_notes || '';
    if (next === current) return;

    try {
      await onUpdate?.({ installer_notes: next });
      toastStore.addToast({ message: 'Notities opgeslagen', type: 'success' });
    } catch {
      toastStore.addToast({ message: 'Kon notities niet opslaan', type: 'error' });
    }
  };

  const saveInternalNotes = async () => {
    try {
      await onUpdate?.({ internal_notes: internalNotes.value });
      toastStore.addToast({ message: 'Interne notities opgeslagen', type: 'success' });
    } catch {
      toastStore.addToast({ message: 'Kon notities niet opslaan', type: 'error' });
    }
  };

  // ── Activities ─────────────────────────────────────────────────────────
  const activities = ref([]);
  const activitiesLoading = ref(true);
  const activitiesSummary = ref(null);

  const loadActivities = async () => {
    try {
      activitiesLoading.value = true;
      const response = await activityService.getActivities(wo.value.id);
      activities.value = response.activities || [];
      activitiesSummary.value = response.summary || null;
    } catch {
      activities.value = wo.value.activities || [];
    } finally {
      activitiesLoading.value = false;
    }
  };

  const totalLoggedMinutes = computed(() =>
    activities.value.reduce((s, a) => s + (a.logged_minutes || 0), 0)
  );

  const totalEstimatedMinutes = computed(() =>
    activities.value.reduce((s, a) => s + (a.estimated_minutes || 0), 0)
  );

  const addActivity = async () => {
    try {
      const a = await activityService.createActivity(wo.value.id, {
        name: 'Nieuwe activiteit',
        activity_type: 'work',
        estimated_minutes: 60,
      });
      activities.value.push({ ...a, editing: true });
    } catch {
      toastStore.addToast({ message: 'Kon activiteit niet toevoegen', type: 'error' });
    }
  };

  const addDefaultActivities = async () => {
    try {
      const res = await activityService.addDefaultActivities(wo.value.id, 'standard');
      activities.value = res.activities || res;
      if (res.summary) activitiesSummary.value = res.summary;
      toastStore.addToast({ message: 'Standaard activiteiten toegevoegd', type: 'success' });
    } catch {
      toastStore.addToast({
        message: 'Kon standaard activiteiten niet toevoegen',
        type: 'error',
      });
    }
  };

  const editActivity = (a) => {
    a.editing = true;
  };

  const saveActivity = async (a) => {
    a.editing = false;
    try {
      await activityService.updateActivity(wo.value.id, a.id, { name: a.name });
    } catch {
      /* noop */
    }
  };

  const updateActivity = async (a) => {
    try {
      await activityService.updateActivity(wo.value.id, a.id, {
        estimated_minutes: a.estimated_minutes,
      });
    } catch {
      /* noop */
    }
  };

  const deleteActivity = async (index) => {
    const a = activities.value[index];
    try {
      await activityService.deleteActivity(wo.value.id, a.id);
      activities.value.splice(index, 1);
    } catch {
      toastStore.addToast({ message: 'Kon activiteit niet verwijderen', type: 'error' });
    }
  };

  const openLogTime = (activity) => {
    const modalId = uiStore.addModal(LogTimeModal, {
      title: `Uren loggen: ${activity.name}`,
      size: 'sm',
      activity,
      onSubmit: async ({ logged_minutes, notes: logNotes }) => {
        try {
          const completed_at = new Date().toISOString();
          const started_at = new Date(Date.now() - logged_minutes * 60 * 1000).toISOString();
          await activityService.logTime(wo.value.id, activity.id, {
            logged_minutes,
            started_at,
            completed_at,
            notes: logNotes,
          });
          activity.logged_minutes = logged_minutes;
          activity.notes = logNotes;
          activity.completed_at = completed_at;
          toastStore.addToast({ message: 'Tijd gelogd', type: 'success' });
          if (getLifecycleStatus(wo.value) === 'open') {
            onUpdate?.({ status: 'in_progress' });
          }
        } catch {
          toastStore.addToast({ message: 'Kon tijd niet loggen', type: 'error' });
        }
        uiStore.closeModal(modalId);
      },
      onCancel: () => uiStore.closeModal(modalId),
    });
  };

  // ── Pakbon ─────────────────────────────────────────────────────────────
  const showPackingSlip = ref(false);
  const packingSlipData = ref(null);
  const packingSlipLoading = ref(false);
  const packingSlipError = ref(null);
  const packingSlipChecked = ref({});
  const downloadingPackingSlip = ref(false);

  // Convert enriched scope productOptions into packing-slip specification rows.
  const optionsToSpecs = (options) =>
    (options || [])
      .filter((o) => o.displayValue != null && o.displayValue !== '')
      .map((o) => ({
        label: o.optionLabel || o.label,
        value: o.displayValue,
        optionType: o.optionType,
        supplierCode: o.choiceSnapshot?.supplierCode || null,
      }));

  // Backend currently returns packing slip items with only label + quantity.
  // We enrich them with specifications coming from the project scope
  // (enriched_snapshot) — see BACKEND_PROJECT_SCOPE_RESPONSE.md / packing slip spec.
  const enrichItemsWithScope = (items, scope) => {
    if (!Array.isArray(items) || !items.length) return items;
    const enriched = scope?._enriched || scope;
    if (!enriched) return items;

    const singleProductSpecs = enriched.selectedProduct
      ? optionsToSpecs(enriched.productOptions)
      : [];

    const productMap = new Map();
    if (Array.isArray(enriched.products)) {
      enriched.products.forEach((p) => {
        const name = (p.productName || p.name || '').trim().toLowerCase();
        if (!name) return;
        productMap.set(name, optionsToSpecs(p.options));
      });
    }

    let appliedSingle = false;

    return items.map((item) => {
      if (item.specifications?.length) return item;
      if (item.category && item.category !== 'product') return item;

      const itemKey = (item.label || '').trim().toLowerCase();
      let specs = productMap.get(itemKey);
      if (!specs && !appliedSingle && singleProductSpecs.length) {
        specs = singleProductSpecs;
        appliedSingle = true;
      }
      if (!specs?.length) return item;

      return { ...item, specifications: specs };
    });
  };

  const loadPackingSlip = async () => {
    try {
      packingSlipLoading.value = true;
      packingSlipError.value = null;

      const projectId = wo.value?.project?.id || wo.value?.project_id;

      const [slip, scopeResponse] = await Promise.all([
        workOrderService.getPackingSlip(wo.value.id),
        projectId
          ? projectService.getScope(projectId).catch(() => null)
          : Promise.resolve(null),
      ]);

      const scope = scopeResponse?.scope || scopeResponse?.data?.scope || scopeResponse;
      packingSlipData.value = {
        ...slip,
        items: enrichItemsWithScope(slip?.items, scope),
      };
    } catch {
      packingSlipError.value = 'Pakbon kon niet geladen worden';
    } finally {
      packingSlipLoading.value = false;
    }
  };

  const togglePackingSlip = (val) => {
    showPackingSlip.value = typeof val === 'boolean' ? val : !showPackingSlip.value;
    if (showPackingSlip.value && !packingSlipData.value) loadPackingSlip();
  };

  const togglePackingSlipItem = (idx) => {
    packingSlipChecked.value[idx] = !packingSlipChecked.value[idx];
  };

  const packingSlipProgress = computed(() => {
    const total = packingSlipData.value?.items?.length || 0;
    if (!total) return null;
    const checked = Object.values(packingSlipChecked.value).filter(Boolean).length;
    return { checked, total, percent: Math.round((checked / total) * 100) };
  });

  const downloadPackingSlip = async () => {
    try {
      downloadingPackingSlip.value = true;
      const blob = await workOrderService.downloadPackingSlipPdf(wo.value.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pakbon-${wo.value.work_order_number}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toastStore.addToast({ message: 'Pakbon gedownload', type: 'success' });
    } catch {
      toastStore.addToast({ message: 'PDF downloaden is nog niet beschikbaar', type: 'warning' });
    } finally {
      downloadingPackingSlip.value = false;
    }
  };

  // ── Scope products (subset of project products per work order) ───────────
  const projectScopeProducts = ref([]);
  const scopeProductsLoading = ref(false);
  const syncingScope = ref(false);

  const projectId = computed(() => wo.value?.project?.id || wo.value?.project_id || null);

  // Normalize a /projects/:id/scope response into a flat list of products
  // with their index (the value used in `product_indexes`).
  const extractScopeProducts = (resp) => {
    const scope = resp?.scope || resp?.data?.scope || resp;
    const list = Array.isArray(scope?.products)
      ? scope.products
      : Array.isArray(resp?.products)
        ? resp.products
        : [];
    return list.map((p, index) => ({
      index,
      productName: p.productName || p.name || `Product ${index + 1}`,
      productSku: p.productSku || p.sku || null,
    }));
  };

  const loadProjectScopeProducts = async () => {
    if (!projectId.value) {
      projectScopeProducts.value = [];
      return [];
    }
    scopeProductsLoading.value = true;
    try {
      const resp = await projectService.getScope(projectId.value);
      projectScopeProducts.value = extractScopeProducts(resp);
      return projectScopeProducts.value;
    } catch {
      projectScopeProducts.value = [];
      return [];
    } finally {
      scopeProductsLoading.value = false;
    }
  };

  // Which products are currently on this work order (indexes into project
  // scope). `null` means the work order carries ALL project products.
  const selectedProductIndexes = computed(() => {
    const idx = wo.value?.scope_product_indexes;
    return Array.isArray(idx) ? idx : null;
  });

  // True when the project scope changed after this work order's snapshot was
  // last synced — drives the "scope gewijzigd / opnieuw syncen" hint.
  const scopeChanged = computed(() => {
    const woSynced = wo.value?.scope_synced_at;
    const projSynced = wo.value?.project?.scope_synced_at;
    if (!woSynced || !projSynced) return false;
    return new Date(woSynced).getTime() < new Date(projSynced).getTime();
  });

  // Apply a work order returned by the API back into parent state and refresh
  // the pakbon (its items may have changed).
  const applyUpdatedWorkOrder = (updated) => {
    if (!updated) return;
    onReplace?.(updated);
    if (showPackingSlip.value || packingSlipData.value) {
      packingSlipData.value = null;
      loadPackingSlip();
    }
  };

  const updateProducts = async (indexes) => {
    const updated = await workOrderService.updateProducts(wo.value.id, indexes);
    applyUpdatedWorkOrder(updated);
    toastStore.addToast({ message: t('work_orders.products.updated'), type: 'success' });
    return updated;
  };

  const syncScopeItems = async () => {
    syncingScope.value = true;
    try {
      const updated = await workOrderService.syncScopeItems(wo.value.id);
      applyUpdatedWorkOrder(updated);
      toastStore.addToast({ message: t('work_orders.products.synced'), type: 'success' });
      return updated;
    } catch {
      toastStore.addToast({ message: t('work_orders.products.sync_error'), type: 'error' });
      throw new Error('sync-failed');
    } finally {
      syncingScope.value = false;
    }
  };

  // ── Photos ─────────────────────────────────────────────────────────────
  const showPhotos = ref(false);
  const photos = ref({ before: [], after: [] });
  const photosUploading = ref({ before: false, after: false });

  watch(
    wo,
    (w) => {
      if (w) {
        photos.value.before = (w.completion_photos || []).map((url) => ({ url }));
      }
    },
    { immediate: true }
  );

  const onPhotoUpload = async (event, phase) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;
    photosUploading.value[phase] = true;
    try {
      const res = await workOrderService.uploadPhotos(wo.value.id, files);
      (res.photos || []).forEach((p) =>
        photos.value[phase].push({ url: p.url, originalName: p.original_name })
      );
      toastStore.addToast({ message: `${files.length} foto('s) geüpload`, type: 'success' });
    } catch {
      toastStore.addToast({ message: 'Foto uploaden mislukt', type: 'error' });
    } finally {
      photosUploading.value[phase] = false;
      event.target.value = '';
    }
  };

  const removePhoto = (phase, index) => {
    photos.value[phase].splice(index, 1);
  };

  const openPhotoPreview = (photo) => {
    window.open(photo.url, '_blank');
  };

  // ── Sign-off ───────────────────────────────────────────────────────────
  const showSignoff = ref(false);
  const signerName = ref('');
  const customerSignature = ref('');
  const signoffNotes = ref('');
  const completingWorkOrder = ref(false);

  // ── Product outcomes (afronden met uitzonderingen) ────────────────────
  // One entry per scope product on this work order. The installer marks
  // each product installed/failed; failed products get a reason and are
  // automatically carried over to a follow-up work order by the backend.
  const productOutcomes = ref([]);

  const buildProductOutcomes = (w) => {
    const items = Array.isArray(w?.scope_items) ? w.scope_items : [];
    const indexes = Array.isArray(w?.scope_product_indexes) ? w.scope_product_indexes : [];
    return items.map((item, position) => ({
      index: indexes.length > 0 ? indexes[position] : position,
      label:
        item?.productName ||
        item?.product_name ||
        item?.selectedProduct?.name ||
        `Product ${position + 1}`,
      status: 'installed',
      reason: '',
      notes: '',
    }));
  };

  // Rebuild only when a different work order is shown (or scope items arrive
  // after hydration) so in-progress selections survive unrelated updates.
  let outcomesBuiltFor = null;
  watch(
    wo,
    (w) => {
      if (!w || w.status === 'completed') return;
      const key = `${w.id}:${(w.scope_items || []).length}`;
      if (key === outcomesBuiltFor) return;
      outcomesBuiltFor = key;
      productOutcomes.value = buildProductOutcomes(w);
    },
    { immediate: true }
  );

  const outcomeReasonOptions = computed(() => [
    { value: '', label: t('work_orders.outcomes.reason_placeholder') },
    { value: 'product_missing', label: t('work_orders.outcomes.reasons.product_missing') },
    { value: 'product_damaged', label: t('work_orders.outcomes.reasons.product_damaged') },
    { value: 'situation_different', label: t('work_orders.outcomes.reasons.situation_different') },
    { value: 'other', label: t('work_orders.outcomes.reasons.other') },
  ]);

  const failedOutcomes = computed(() =>
    productOutcomes.value.filter((o) => o.status === 'failed')
  );
  const hasFailedOutcomes = computed(() => failedOutcomes.value.length > 0);

  // Failed products need a reason before the work order can be completed.
  const outcomesIncomplete = computed(() => failedOutcomes.value.some((o) => !o.reason));

  const setOutcomeStatus = (outcome, status) => {
    outcome.status = status;
    if (status === 'installed') {
      outcome.reason = '';
      outcome.notes = '';
    }
  };

  // ── On-site payment link (reopen QR after dismissing completion modal) ──
  const onSitePaymentOverride = ref(null);
  const loadingPaymentLink = ref(false);

  const onSitePayment = computed(
    () => onSitePaymentOverride.value || wo.value?.on_site_payment || null
  );

  const canShowPaymentLink = computed(
    () => wo.value?.status === 'completed' && !!onSitePayment.value?.available
  );

  const openPaymentModal = (autoInvoice, paymentLink) => {
    const modalId = uiStore.addModal(WorkOrderPaymentModal, {
      title: t('mollie.work_order_complete.title'),
      size: 'md',
      autoInvoice,
      paymentLink,
      onClose: () => uiStore.closeModal(modalId),
    });
  };

  const showPaymentLink = async () => {
    if (!wo.value?.id || loadingPaymentLink.value) return;
    loadingPaymentLink.value = true;
    try {
      const result = await workOrderService.getPaymentLink(wo.value.id);
      if (result?.auto_invoice) {
        onSitePaymentOverride.value = {
          available: true,
          invoice_id: result.auto_invoice.id,
          invoice_number: result.auto_invoice.invoice_number,
        };
      }
      openPaymentModal(result?.auto_invoice || null, result?.payment_link || null);
    } catch (error) {
      const msg =
        error?.response?.data?.error || t('mollie.work_order_complete.payment_link_error');
      toastStore.addToast({ message: msg, type: 'error' });
    } finally {
      loadingPaymentLink.value = false;
    }
  };

  const completeWorkOrder = async () => {
    try {
      completingWorkOrder.value = true;

      const payload = {
        completion_notes: signoffNotes.value,
        signer_name: signerName.value,
        signature: customerSignature.value,
        photo_urls: [
          ...photos.value.before.map((p) => p.url),
          ...photos.value.after.map((p) => p.url),
        ],
      };

      if (productOutcomes.value.length > 0) {
        payload.product_outcomes = productOutcomes.value.map((o) => {
          const entry = { product_index: o.index, status: o.status };
          if (o.status === 'failed') {
            entry.reason = o.reason;
            if (o.notes) entry.notes = o.notes;
          }
          return entry;
        });
        payload.carry_over = { enabled: true };
      }

      const response = await workOrderService.completeWorkOrder(wo.value.id, payload);

      const followUp = response?.carry_over_work_order || null;
      toastStore.addToast({
        message: followUp
          ? t('work_orders.outcomes.completed_with_follow_up', {
              number: followUp.work_order_number,
            })
          : 'Werkbon afgetekend!',
        type: 'success',
      });

      // Sprint 3: when the backend creates a final invoice AND Mollie is
      // connected, the response carries `auto_invoice` + `payment_link`.
      // Show the field "betaal nu"-modal so the engineer can let the
      // customer scan the QR on the spot.
      const paymentLink = response?.payment_link || null;
      const autoInvoice = response?.auto_invoice || null;
      if (paymentLink) {
        if (autoInvoice) {
          onSitePaymentOverride.value = {
            available: true,
            invoice_id: autoInvoice.id,
            invoice_number: autoInvoice.invoice_number,
          };
        }
        openPaymentModal(autoInvoice, paymentLink);
        // Auto-refresh the work-order detail after 5s so the installer sees
        // the updated invoice state once the customer pays.
        setTimeout(() => {
          onComplete?.();
        }, 5000);
      } else {
        onComplete?.();
      }
    } catch {
      toastStore.addToast({ message: 'Aftekenen mislukt', type: 'error' });
    } finally {
      completingWorkOrder.value = false;
    }
  };

  // ── Configurator forms ─────────────────────────────────────────────────
  const configuratorForms = ref([]);
  const selectedFormId = ref(null);

  const dealId = computed(() => wo.value?.project?.deal_id || null);

  const formOptions = computed(() => {
    const opts = configuratorForms.value.map((f) => ({ value: f.id, label: f.name }));
    return configuratorForms.value.length > 1
      ? [{ value: '', label: t('work_orders.select_form') }, ...opts]
      : opts;
  });

  const activeFormId = computed(() =>
    configuratorForms.value.length === 1 ? configuratorForms.value[0].id : selectedFormId.value
  );

  const fetchForms = async () => {
    try {
      const res = await formService.getForms();
      const forms = (res.forms || res || []).filter((f) => f.type === 'configurator');
      configuratorForms.value = forms;
      if (forms.length === 1) selectedFormId.value = forms[0].id;
      else if (forms.length > 1) selectedFormId.value = '';
    } catch {
      /* noop */
    }
  };

  watch(
    dealId,
    (id) => {
      if (id) fetchForms();
    },
    { immediate: true }
  );

  const openConfiguratorForm = () => {
    const formId = activeFormId.value;
    if (!formId || !dealId.value) return;
    const form = configuratorForms.value.find((f) => String(f.id) === String(formId));
    if (!form) return;
    const baseUrl = getFormUrl(userStore.currentOrganization, form.id);
    const userId = userStore.currentUser?.id || '';
    window.open(
      `${baseUrl}?deal_id=${dealId.value}&mode=extended&proposal_stage=${PROPOSAL_STAGES.MEASURED}` +
        `&revised_by=${userId}&revised_from=work_order`,
      '_blank'
    );
  };

  // ── Computed helpers ───────────────────────────────────────────────────
  const hasUsableAddress = (addr) => {
    if (!addr || typeof addr !== 'object') return false;
    return Boolean(
      addr.street ||
        addr.house_number ||
        addr.houseNumber ||
        addr.city ||
        addr.postal_code ||
        addr.postalCode
    );
  };

  const installationAddress = computed(() => {
    const woAddr = wo.value?.installation_address;
    const projectAddr = wo.value?.project?.installation_address;
    if (hasUsableAddress(woAddr)) return woAddr;
    if (hasUsableAddress(projectAddr)) return projectAddr;
    return null;
  });

  const mapUrl = computed(() => {
    if (!installationAddress.value) return null;
    const a = installationAddress.value;
    const q = encodeURIComponent(
      `${a.street} ${a.house_number || a.houseNumber || ''}, ${a.postal_code || a.postalCode || ''} ${a.city}`
    );
    return `https://maps.google.com/maps?q=${q}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
  });

  const googleMapsLink = computed(() => {
    if (!installationAddress.value) return null;
    const a = installationAddress.value;
    const q = encodeURIComponent(
      `${a.street} ${a.house_number || a.houseNumber || ''}, ${a.postal_code || a.postalCode || ''} ${a.city}`
    );
    return `https://www.google.com/maps/search/?api=1&query=${q}`;
  });

  const navigationLink = computed(() => {
    if (!installationAddress.value) return null;
    const a = installationAddress.value;
    const dest = encodeURIComponent(
      [a.street, a.house_number || a.houseNumber, a.postal_code || a.postalCode, a.city]
        .filter(Boolean)
        .join(' ')
    );
    return `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
  });

  const customer = computed(() => {
    const lead = wo.value?.project?.lead;
    if (!lead) return null;
    return {
      name: lead.name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim(),
      phone: lead.phone,
      email: lead.email,
    };
  });

  const formatUserName = (user) => {
    if (!user || typeof user !== 'object') return null;
    return user.first_name
      ? `${user.first_name} ${user.last_name || ''}`.trim()
      : user.email || null;
  };

  const assignedUsers = computed(() => {
    if (wo.value?.assigned_users?.length > 0) return wo.value.assigned_users;
    const single = wo.value?.assigned_user || wo.value?.assigned_to;
    return single && typeof single === 'object' ? [single] : [];
  });

  const installerName = computed(() => {
    if (assignedUsers.value.length > 0) {
      return assignedUsers.value.map(formatUserName).filter(Boolean).join(', ') || 'Toegewezen';
    }
    return wo.value?.assigned_to || wo.value?.assigned_user ? 'Toegewezen' : 'Niet toegewezen';
  });

  const appointmentDate = computed(() => getAppointmentStart(wo.value));

  const isTentative = computed(() => isTentativelyPlanned(wo.value));

  const canSchedule = computed(() =>
    !['completed', 'cancelled'].includes(getLifecycleStatus(wo.value))
  );

  // ── Status confirmation ────────────────────────────────────────────────
  // "Klaar voor installatie" is geen levenscyclus-status meer. De API
  // bewaart dat als materials_ready_at; derived_state volgt daaruit.
  const markReadyForInstallation = async () => {
    try {
      const payload = {
        materials_ready_at: new Date().toISOString(),
        materials_ready_source: 'manual',
      };
      const updated = await workOrderService.updateWorkOrder(wo.value.id, payload);
      Object.assign(wo.value, {
        materials_ready_at: updated?.materials_ready_at || payload.materials_ready_at,
        materials_ready_source: updated?.materials_ready_source || payload.materials_ready_source,
        derived_state: undefined,
      });
      toastStore.addToast({ message: 'Werkbon klaar voor installatie', type: 'success' });
      return true;
    } catch {
      toastStore.addToast({
        message: 'Kon status niet wijzigen naar klaar voor installatie',
        type: 'error',
      });
      return false;
    }
  };

  // v2: starten kan ook zonder afspraak (spoedservice)
  const canStart = computed(() => getLifecycleStatus(wo.value) === 'open');

  const startWorkOrder = async () => {
    if (!canStart.value) return;
    try {
      await workOrderService.startWorkOrder(wo.value.id);
      wo.value.status = 'in_progress';
      toastStore.addToast({ message: 'Werkbon gestart', type: 'success' });
    } catch {
      toastStore.addToast({ message: 'Kon werkbon niet starten', type: 'error' });
    }
  };

  // ── Format helpers ─────────────────────────────────────────────────────
  const getStatusLabel = (status) =>
    ({
      pending: 'In afwachting',
      scheduled: 'Ingepland',
      ready_for_installation: 'Klaar voor installatie',
      in_progress: 'Bezig',
      completed: 'Voltooid',
      cancelled: 'Geannuleerd',
    })[status] || status;

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString('nl-NL', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
        })
      : '';

  const formatTime = (d) =>
    d ? new Date(d).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : '';

  const formatMinutes = (m) => {
    if (m === null || m === undefined) return '--:--';
    return `${Math.floor(m / 60)}:${(m % 60).toString().padStart(2, '0')}`;
  };

  const formatFullAddress = (addr) => {
    if (!addr) return '';
    const street = [addr.street, addr.house_number || addr.houseNumber].filter(Boolean).join(' ');
    const city = addr.city || '';
    return [street, city].filter(Boolean).join(', ');
  };

  // ── Init ───────────────────────────────────────────────────────────────
  onMounted(loadActivities);

  return {
    // Notes
    installerNotes,
    internalNotes,
    saveInstallerNotes,
    saveInternalNotes,
    // Activities
    activities,
    activitiesLoading,
    activitiesSummary,
    loadActivities,
    totalLoggedMinutes,
    totalEstimatedMinutes,
    addActivity,
    addDefaultActivities,
    editActivity,
    saveActivity,
    updateActivity,
    deleteActivity,
    openLogTime,
    // Pakbon
    showPackingSlip,
    packingSlipData,
    packingSlipLoading,
    packingSlipError,
    packingSlipChecked,
    packingSlipProgress,
    downloadingPackingSlip,
    togglePackingSlip,
    togglePackingSlipItem,
    downloadPackingSlip,
    // Scope products
    projectScopeProducts,
    scopeProductsLoading,
    syncingScope,
    loadProjectScopeProducts,
    selectedProductIndexes,
    scopeChanged,
    updateProducts,
    syncScopeItems,
    // Photos
    showPhotos,
    photos,
    photosUploading,
    onPhotoUpload,
    removePhoto,
    openPhotoPreview,
    // Sign-off
    showSignoff,
    signerName,
    customerSignature,
    signoffNotes,
    completingWorkOrder,
    completeWorkOrder,
    canShowPaymentLink,
    loadingPaymentLink,
    showPaymentLink,
    // Product outcomes (afronden met uitzonderingen)
    productOutcomes,
    outcomeReasonOptions,
    failedOutcomes,
    hasFailedOutcomes,
    outcomesIncomplete,
    setOutcomeStatus,
    // Forms
    dealId,
    configuratorForms,
    selectedFormId,
    formOptions,
    activeFormId,
    openConfiguratorForm,
    // Computed
    installationAddress,
    mapUrl,
    googleMapsLink,
    navigationLink,
    customer,
    assignedUsers,
    installerName,
    appointmentDate,
    isTentative,
    canSchedule,
    canStart,
    markReadyForInstallation,
    startWorkOrder,
    // Formatters
    getStatusLabel,
    formatDate,
    formatTime,
    formatMinutes,
    formatFullAddress,
  };
}
